import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { Ban, BotOff, PowerOff } from "lucide-react"
import { useNewSupportFlow } from "modules/auth/hooks/useNewSupportFlow"
import { CampaignFlowBadge } from "modules/campaigns/components/Detail/CampaignFlowBadge"
import { CompanyClientCell } from "modules/customers/components/CompanyClientCell"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { CustomerCell } from "modules/shared/components/CustomerCell"
import { ChannelIcon } from "modules/shared/components/Miscelanea/ChannelIcon"
import { CustomerLocale } from "modules/shared/components/Miscelanea/CustomerLocale"
import { Pulse } from "modules/shared/components/Miscelanea/Pulse"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { SearchSchema } from "routes/_base/_auth/conversations/list/route"
import {
  ConversationsResponseDataSchema,
  ConversationStatus,
  ConversationTypeEnum,
  GetConversationsSortFields,
} from "schema"
import { z } from "zod"
import { ConversationStatusBadge } from "../ConversationStatusBadge"

type Props = {
  campaignId?: string
  data: z.infer<typeof ConversationsResponseDataSchema> | undefined
  isPending: boolean
  fields: typeof GetConversationsSortFields
  onSort: (field: (typeof GetConversationsSortFields)[number]) => void
  search: z.infer<typeof SearchSchema>
  isTesting?: boolean
  showCampaign?: boolean
}

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof ConversationsResponseDataSchema>[number]
>()

function getSupportStatus({
  createdAt,
  enabledAt,
  hasIssueUpdates,
  hasNewIssues,
}: {
  createdAt: string
  enabledAt: string | null
  hasIssueUpdates?: boolean
  hasNewIssues?: boolean
}) {
  const isNewSupportFlowConversation =
    enabledAt !== null && new Date(createdAt).getTime() >= new Date(enabledAt).getTime()

  if (!isNewSupportFlowConversation) return undefined
  if (hasNewIssues) return "Issue" as const
  if (hasIssueUpdates) return "IssueUpdate" as const
  return "Deflected" as const
}

export function ConversationsTable({
  campaignId,
  data,
  isPending,
  fields,
  onSort,
  search,
  isTesting = false,
  showCampaign = true,
}: Props) {
  const { t } = useTranslation()
  const isAmphoraCompany = useIsAmphoraCompany()
  const { enabledAt: newSupportFlowEnabledAt } = useNewSupportFlow()

  const columns = useMemo(
    () =>
      columnHelper.columns(
        [
          columnHelper.display({
            id: "resolvedAutomatically",
            header: () => null,
            cell: ({ row }) => {
              const conversationIsActive = !row.original.closedAt
              const isAIDisabled = row.original.aiDisabled
              const isDenylisted = row.original.customer.denylistedAt
              const isOptedOut = row.original.customer.optedOutAt

              let content = null
              if (isDenylisted) {
                content = (
                  <Tooltip>
                    <TooltipTrigger>
                      <Ban className="size-4" />
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {t("conversations.table.denylistedTooltip")}
                    </TooltipContent>
                  </Tooltip>
                )
              } else if (isOptedOut) {
                content = (
                  <Tooltip>
                    <TooltipTrigger>
                      <PowerOff className="size-4" />
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {t("conversations.table.optedOutTooltip")}
                    </TooltipContent>
                  </Tooltip>
                )
              } else if (isAIDisabled) {
                content = (
                  <Tooltip>
                    <TooltipTrigger>
                      <BotOff className="size-4" />
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {t("conversations.table.aiDisabledTooltip")}
                    </TooltipContent>
                  </Tooltip>
                )
              } else if (conversationIsActive) {
                content = (
                  <Tooltip>
                    <TooltipTrigger>
                      <Pulse className={cn("fill-chart-1", isTesting && "fill-testing-1")} />
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {t("conversations.active.explanation")}
                    </TooltipContent>
                  </Tooltip>
                )
              }

              return content
            },
            meta: {
              className: "pr-3",
            },
          }),
          !isAmphoraCompany &&
            columnHelper.display({
              id: "customer",
              header: t("conversations.table.customer"),
              cell: ({ row }) => {
                return <CustomerCell customer={row.original.customer} />
              },
              meta: { colSpan: 2 },
            }),
          isAmphoraCompany &&
            columnHelper.display({
              id: "client",
              header: t("conversations.table.client"),
              cell: ({ row }) => <CompanyClientCell client={row.original.customer.client} />,
            }),
          isAmphoraCompany &&
            columnHelper.accessor("orderId", {
              header: t("conversations.table.orderId"),
              cell: ({ getValue }) => getValue() ?? "-",
            }),
          isTesting &&
            columnHelper.accessor("type", {
              header: t("conversations.table.type"),
              cell: () => {
                return (
                  <Badge variant="secondary" className="px-1.5 bg-gradient-testing text-background">
                    {ConversationTypeEnum.Testing}
                  </Badge>
                )
              },
            }),
          showCampaign &&
            columnHelper.accessor("campaign", {
              header: t("conversations.table.campaign"),
              cell: ({ row }) => {
                const campaign = "campaign" in row.original ? row.original.campaign : undefined
                if (!campaign)
                  return (
                    <span className="text-muted-foreground font-italic">
                      {t("conversations.table.noCampaign")}
                    </span>
                  )
                if (!campaign.name)
                  return <span className="text-muted-foreground">{t("campaigns.untitled")}</span>
                return campaign.name
              },
            }),
          showCampaign &&
            columnHelper.accessor("channel", {
              header: t("conversations.table.channel"),
              cell: (props) => {
                const channel = props.getValue()
                return <ChannelIcon channel={channel} withTooltip />
              },
              meta: { colSpan: 1, align: "center" },
            }),
          columnHelper.accessor("conversationFlow", {
            header: t("conversations.table.flow"),
            cell: ({ getValue }) => {
              const conversationFlow = getValue()
              if (!conversationFlow) return <span className="text-muted-foreground">{"-"}</span>
              return <CampaignFlowBadge flow={conversationFlow} iconOnly />
            },
            meta: { colSpan: 1, align: "center" },
          }),
          columnHelper.accessor("status", {
            header: t("conversations.table.status"),
            cell: ({ row }) => {
              const status = "status" in row.original ? row.original.status : undefined
              if (!status) return "-"
              const supportStatus = getSupportStatus({
                createdAt: row.original.createdAt,
                enabledAt: newSupportFlowEnabledAt,
                hasIssueUpdates: row.original.hasIssueUpdates,
                hasNewIssues: row.original.hasNewIssues,
              })

              return (
                <ConversationStatusBadge
                  status={status as ConversationStatus}
                  failedReason={row.original.failedReason}
                  whatsappRoute={row.original.whatsappRoute}
                  conversationFlow={row.original.conversationFlow}
                  hasTickets={row.original.hasTickets}
                  supportStatus={supportStatus}
                />
              )
            },
          }),
          columnHelper.display({
            id: "locale",
            header: t("conversations.table.locale"),
            cell: ({ row }) => <CustomerLocale {...row.original.customer} />,
          }),
          columnHelper.accessor("createdAt", {
            header: t("conversations.table.reachedAt"),
            cell: ({ getValue }) => <DateDisplay date={getValue()} />,
            meta: { align: "right" },
          }),
          columnHelper.accessor("customer.lastMessageSentAt", {
            id: "customer.lastMessageSentAt",
            header: t("conversations.table.lastMessageSentAt"),
            cell: ({ getValue }) => {
              const value = getValue()

              if (!value) {
                return "-"
              }
              return <DateDisplay date={value} />
            },
            meta: { align: "right" },
          }),
        ].filter(Boolean),
      ),
    [t, isAmphoraCompany, isTesting, showCampaign, newSupportFlowEnabledAt],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function getTo() {
    if (!campaignId) return "/conversations/list/$customerId" as const
    return "/campaigns/$id/insights" as const
  }

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("conversations.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.id}
        navigate={{
          to: getTo(),
          params: campaignId
            ? { id: campaignId, customerId: row.original.customer.id }
            : { customerId: row.original.customer.id },
          search: {
            ...search,
            conversationId: Number(row.original.id),
            lastRow: row.id,
            openPlayground: campaignId ? true : undefined,
          },
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(
              cell.column.columnDef.meta?.className,
              "lastRow" in search && String(search.lastRow) === row.id && "bg-muted",
            )}
            {...cell.column.columnDef.meta}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<Features, z.infer<typeof ConversationsResponseDataSchema>[number], unknown>,
  ) {
    if (!fields.includes(header.id as (typeof GetConversationsSortFields)[number])) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "desc",
      onClick: () => onSort(header.id as (typeof GetConversationsSortFields)[number]),
    }
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
